import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Users, CreditCard, Coins, Film, Import, Eye, AlertTriangle, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";

export default function Admin() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/config").then((r) => setConfig(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data.users || [])).catch(() => {});
  }, [user]);

  if (user === null) return null;
  if (user === false || user?.role !== "admin") return <Navigate to="/dashboard" replace />;

  const saveCosts = async () => {
    try { await api.patch("/admin/config/credit-costs", { value: config.credit_costs }); toast.success("Credit costs saved"); }
    catch { toast.error("Save failed"); }
  };
  const savePlans = async () => {
    try { await api.patch("/admin/config/plans", { value: config.plans }); toast.success("Plans saved"); }
    catch { toast.error("Save failed"); }
  };
  const saveFlags = async (flags) => {
    setConfig({ ...config, feature_flags: flags });
    try { await api.patch("/admin/config/feature-flags", { value: flags }); toast.success("Flags updated"); }
    catch { toast.error("Save failed"); }
  };

  const statCards = stats ? [
    { icon: Users, label: "Total users", value: stats.users.total, sub: `${stats.users.paid} paid · ${stats.users.free} free` },
    { icon: TrendingUp, label: "Revenue", value: `£${stats.revenue}`, sub: "Paid transactions" },
    { icon: Coins, label: "Credits consumed", value: stats.credits_consumed },
    { icon: Film, label: "Clips generated", value: stats.clips_generated },
    { icon: Import, label: "Campaign imports", value: stats.campaign_imports },
    { icon: Eye, label: "Campaign views", value: stats.campaign_views },
    { icon: CreditCard, label: "Videos processed", value: stats.videos_processed },
    { icon: AlertTriangle, label: "Processing failures", value: stats.processing_failures },
  ] : [];

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="inline-flex px-2.5 py-1 rounded-full bg-violet-500/10 text-violet-400 border border-violet-500/30 text-[10px] font-mono uppercase tracking-widest mb-2">Admin</div>
        <h1 className="font-display text-3xl font-bold mb-6">Operations Dashboard</h1>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {statCards.map((s) => (
            <div key={s.label} className="bg-[#141419] border border-white/10 rounded-2xl p-5" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
              <s.icon className="w-5 h-5 text-violet-400 mb-3" />
              <p className="font-display text-2xl font-bold">{s.value}</p>
              <p className="text-xs text-zinc-500 mt-1">{s.label}</p>
              {s.sub && <p className="text-[10px] text-zinc-600 mt-0.5">{s.sub}</p>}
            </div>
          ))}
        </div>

        <Tabs defaultValue="config">
          <TabsList className="bg-[#141419] border border-white/10">
            <TabsTrigger value="config" data-testid="admin-tab-config">Configuration</TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users">Users ({users.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="config" className="mt-6 space-y-6">
            {config && (
              <>
                <div className="bg-[#141419] border border-white/10 rounded-2xl p-6">
                  <h3 className="font-display font-bold mb-4">Credit Costs</h3>
                  <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-4">
                    {Object.entries(config.credit_costs).map(([k, v]) => (
                      <div key={k}>
                        <Label className="text-xs text-zinc-400 capitalize">{k.replace(/_/g, " ")}</Label>
                        <Input type="number" value={v} data-testid={`cost-${k}`} onChange={(e) => setConfig({ ...config, credit_costs: { ...config.credit_costs, [k]: parseInt(e.target.value) || 0 } })} className="bg-zinc-900 border-white/10 mt-1.5" />
                      </div>
                    ))}
                  </div>
                  <Button onClick={saveCosts} data-testid="save-costs-btn" className="mt-4 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold">Save Credit Costs</Button>
                </div>

                <div className="bg-[#141419] border border-white/10 rounded-2xl p-6">
                  <h3 className="font-display font-bold mb-4">Plan Credits & Limits</h3>
                  <div className="space-y-3">
                    {Object.values(config.plans).sort((a, b) => a.order - b.order).map((p) => (
                      <div key={p.key} className="grid grid-cols-2 sm:grid-cols-5 gap-3 items-end border-b border-white/5 pb-3">
                        <div className="font-semibold text-sm">{p.name}</div>
                        <div>
                          <Label className="text-[10px] text-zinc-500">Price £</Label>
                          <Input type="number" value={p.price} data-testid={`plan-price-${p.key}`} onChange={(e) => setConfig({ ...config, plans: { ...config.plans, [p.key]: { ...p, price: parseInt(e.target.value) || 0 } } })} className="bg-zinc-900 border-white/10 h-9" />
                        </div>
                        <div>
                          <Label className="text-[10px] text-zinc-500">Credits</Label>
                          <Input type="number" value={p.credits} data-testid={`plan-credits-${p.key}`} onChange={(e) => setConfig({ ...config, plans: { ...config.plans, [p.key]: { ...p, credits: parseInt(e.target.value) || 0 } } })} className="bg-zinc-900 border-white/10 h-9" />
                        </div>
                        <div>
                          <Label className="text-[10px] text-zinc-500">Max min</Label>
                          <Input type="number" value={p.max_video_minutes} onChange={(e) => setConfig({ ...config, plans: { ...config.plans, [p.key]: { ...p, max_video_minutes: parseInt(e.target.value) || 0 } } })} className="bg-zinc-900 border-white/10 h-9" />
                        </div>
                        <div>
                          <Label className="text-[10px] text-zinc-500">Max MB</Label>
                          <Input type="number" value={p.max_upload_mb} onChange={(e) => setConfig({ ...config, plans: { ...config.plans, [p.key]: { ...p, max_upload_mb: parseInt(e.target.value) || 0 } } })} className="bg-zinc-900 border-white/10 h-9" />
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button onClick={savePlans} data-testid="save-plans-btn" className="mt-4 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold">Save Plans</Button>
                  <p className="text-xs text-zinc-600 mt-2">Note: changing display price updates the UI & credit allocation. Stripe checkout amounts are set in the payment catalog.</p>
                </div>

                <div className="bg-[#141419] border border-white/10 rounded-2xl p-6">
                  <h3 className="font-display font-bold mb-4">Feature Flags</h3>
                  <div className="space-y-3">
                    {Object.entries(config.feature_flags).map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between">
                        <Label className="text-sm text-zinc-300 capitalize">{k.replace(/_/g, " ")}</Label>
                        <Switch checked={v} data-testid={`flag-${k}`} onCheckedChange={(val) => saveFlags({ ...config.feature_flags, [k]: val })} />
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="users" className="mt-6">
            <div className="bg-[#141419] border border-white/10 rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-zinc-400 text-xs uppercase font-mono">
                  <tr>
                    <th className="text-left p-3">Email</th>
                    <th className="text-left p-3">Name</th>
                    <th className="text-left p-3">Plan</th>
                    <th className="text-left p-3">Credits</th>
                    <th className="text-left p-3">Role</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-t border-white/5">
                      <td className="p-3">{u.email}</td>
                      <td className="p-3 text-zinc-400">{u.name}</td>
                      <td className="p-3"><span className={`px-2 py-0.5 rounded text-xs ${u.plan === "free" ? "bg-zinc-700/30 text-zinc-400" : "bg-amber-500/15 text-amber-400"}`}>{u.plan}</span></td>
                      <td className="p-3 text-zinc-400">{u.credits}</td>
                      <td className="p-3 text-zinc-500">{u.role}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
